/**
 * Writing one event into one voice, as arithmetic on a list.
 *
 * Entry is overwriting, not insertion. Typing a crotchet where a minim stands leaves the
 * bar exactly as long as it was and turns the rest of the minim into silence; it does not
 * push the remainder of the bar past the bar line. That is what every notation editor
 * does, and it is the only behaviour that keeps a bar engravable while it is being typed
 * into -- an insertion would leave every bar after the caret overfull until the user
 * happened to even it out again.
 *
 * Nothing here knows about the score, the caret or commands. It takes a voice, a moment and
 * an event, and answers with the events the voice should hold, or with a sentence saying
 * why it cannot. The caller turns that into an edit.
 */

import { fillTicks } from "../document/duration";
import { rest } from "../document/create";
import { eventOnsets, occupiedTicks } from "../document/query";
import type { MusicEvent, Voice } from "../document/types";

/** A voice's new contents, and where in them the written event landed. */
export interface Written {
  readonly events: readonly MusicEvent[];
  /** Index of the event that was just written, for a caret that wants to name it. */
  readonly index: number;
}

/** Rests totalling `ticks`, or `null` when that span cannot be written as plain notes. */
export function silence(ticks: number): readonly MusicEvent[] | null {
  if (ticks === 0) return [];
  const durations = fillTicks(ticks);
  return durations === null ? null : durations.map((duration) => rest(duration));
}

/** Where each event of a voice starts and ends, with a whole-measure rest filling the bar. */
function spans(voice: Voice, capacity: number): readonly { start: number; end: number }[] {
  const onsets = eventOnsets(voice);
  return voice.events.map((event, at) => {
    const start = onsets[at] ?? 0;
    return { start, end: start + occupiedTicks(event, capacity) };
  });
}

/**
 * Put `event` into `voice` at `onset`, overwriting whatever sounds there.
 *
 * Anything the new event covers is removed. What is left of an event it only partly covers
 * becomes rests, at both ends: writing a quaver into the middle of a minim leaves a quaver
 * of silence before it and five quavers after. A gap between the end of the voice and the
 * onset is filled with rests too, so that writing into the second half of an empty bar
 * does not leave the first half unaccounted for.
 */
export function overwrite(
  voice: Voice,
  onset: number,
  event: MusicEvent,
  capacity: number,
): Written | string {
  const length = occupiedTicks(event, capacity);
  if (onset < 0 || !Number.isInteger(onset)) return "a note can only be written on a tick";
  if (length <= 0) return "a note with no length cannot be written on its own";
  if (onset + length > capacity) return "that note is longer than what is left of the bar";

  const end = onset + length;
  const placed = spans(voice, capacity);
  const before: MusicEvent[] = [];
  const after: MusicEvent[] = [];
  let leading = 0;
  let trailing = 0;

  voice.events.forEach((existing, at) => {
    const span = placed[at];
    if (span === undefined) return;
    // A grace note has no length of its own. One standing at the onset stays where it is
    // and leans on the note now written there; one inside the span it covers has lost the
    // note it leaned on, and is kept rather than thrown away, leaning on what comes next.
    if (span.end <= onset) {
      before.push(existing);
      return;
    }
    if (span.start >= end || span.end === span.start) {
      after.push(existing);
      return;
    }
    leading = Math.max(leading, onset - span.start);
    trailing = Math.max(trailing, span.end - end);
  });

  const reach = before.reduce((total, existing) => total + occupiedTicks(existing, capacity), 0);
  const gap = silence(Math.max(0, onset - reach - leading));
  const head = silence(leading);
  const tail = silence(trailing);
  if (gap === null || head === null || tail === null) {
    return "what is left of the bar cannot be written as plain rests";
  }

  const events = [...before, ...gap, ...head, event, ...tail, ...after];
  return { events, index: before.length + gap.length + head.length };
}

/**
 * Take an event out of a voice, leaving silence of the same length in its place.
 *
 * Deleting is not removal: a bar that was full stays full, because the alternative is a bar
 * that shortens itself every time a note is deleted from it. A whole-measure rest is left
 * alone, since there is nothing behind it to uncover.
 */
export function erase(voice: Voice, index: number, capacity: number): Written | string {
  const event = voice.events[index];
  if (event === undefined) return "there is no note there";
  if (event.kind === "rest" && event.wholeMeasure) return { events: voice.events, index };

  const silent = silence(occupiedTicks(event, capacity));
  if (silent === null) return "that note cannot be replaced by plain rests";
  const events = [...voice.events.slice(0, index), ...silent, ...voice.events.slice(index + 1)];
  return { events, index };
}
